import { useState } from "react";
import {
  isDatasheetPage,
  isProductPage,
  userOnMyAccountPage,
  userOnRegistrationConfirmatioAccountPage,
  userOnRegistrationConfirmationEmailPage,
} from "~/utils/url/urlUtils";
import {
  displayAffiliatePartnerPopupBanner,
  saveAffiliatePopupClosedCookie,
  showAffiliatePopupBanner,
} from "~/domains/affiliate/utils/utils";
import { useOnMount } from "~/common/hooks/useOnMount";
import { trackAffiliatePopupLoad } from "~/app/analytics/ga/affiliate";
import Modal from "~/common/components/Modal";
import { MediasLogo } from "~/features/navigation/components/TopBar/MediasLogo";
import { CompanyLogo } from "~/domains/company/components/CompanyLogo";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import { Box, Button, GlobalStyles, Stack, Typography, styled, useTheme } from "~/common/mui";
import { selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import { useSelector } from "react-redux";
import { trackAffiliateCADRedirect } from "~/app/analytics/ga/ga";
import { ProductService } from "~/features/addToCart/api/ProductService";

const AffiliateCardLogo = styled("img")(({ theme }) => ({
  width: 60,
  height: "auto",
  [theme.breakpoints.up("desktop-s")]: {
    width: 200,
  },
}));

const AffiliateCardSeparator = styled(Box)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  height: 60,
  margin: `0 ${theme.spacing(1.5)}`,
}));

const PartnerCard = styled(Card)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(3.75),
  [theme.breakpoints.up("desktop-s")]: {
    padding: `${theme.spacing(2.5)} ${theme.spacing(3.75)}`,
  },
}));

const PopupHeader = styled(Stack)(({ theme }) => ({
  paddingBottom: theme.spacing(3),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3.75),
}));

export interface AffiliateSalesPartnerPopupViewProps {
  className?: string;
  sx?: import("~/common/mui").SxProps;
  title: string;
  redirectText: string;
  okLabel: string;
  partnerName?: string;
  partnerLogo?: string;
  fullWidthButton: boolean;
  onClose: () => void;
}

export const AffiliateSalesPartnerPopupView = ({
  className,
  sx,
  title,
  redirectText,
  okLabel,
  partnerName,
  partnerLogo,
  fullWidthButton,
  onClose,
}: AffiliateSalesPartnerPopupViewProps) => {
  const theme = useTheme();

  return (
    <Box className={className} sx={sx}>
      <Modal
        onClose={onClose}
        footerChildren={
          <Button fullWidth={fullWidthButton} onClick={onClose} data-test-id={"close-modal-partner"}>
            {okLabel}
          </Button>
        }
      >
        <GlobalStyles
          styles={{
            "&.scha__modal__full-screen-view": {
              "& .scha__modal__contents": {
                flex: 1,
              },
              "& .scha__modal__footer": {
                borderTop: `1px solid ${theme.palette.divider}`,
              },
            },
          }}
        />
        <PopupHeader direction="row" justifyContent="space-between" alignItems="center">
          <MediasLogo variant={"general"} />
          <CompanyLogo className={"scha__header__logo"} />
        </PopupHeader>
        <Typography variant="headlineSmall" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="titleMedium" sx={{ mb: 3.75 }}>
          {redirectText}
        </Typography>
        <PartnerCard disableHover={true}>
          {partnerLogo && <AffiliateCardLogo src={partnerLogo} />}
          {partnerLogo && <AffiliateCardSeparator />}
          <Typography sx={{ m: 0, py: 3, fontWeight: "bold" }}>{partnerName}</Typography>
        </PartnerCard>
      </Modal>
    </Box>
  );
};

export const AffiliateSalesPartnerPopup = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(showAffiliatePopupBanner());

  const partner = useSelector(selectVisitedAffiliate);

  const isDatasheet = isDatasheetPage();

  useOnMount(() => {
    if (partner && isOpen) {
      trackAffiliatePopupLoad(partner?.name ?? "");
    }
  });

  const isMinDesktop = useIsMinDesktopSize();

  if (!isOpen || isDatasheet) {
    return null;
  }

  const onClose = () => {
    const isPdp = isProductPage();
    setIsOpen(false);
    saveAffiliatePopupClosedCookie();
    if (isPdp && partner) {
      const productCode = window.location.pathname.split("/p/")[1];
      const productData = ProductService.getBaseProductData(productCode);
      trackAffiliateCADRedirect(
        productData?.designation ?? "",
        productData?.code ?? "",
        partner.name,
        partner.affiliateId,
      );
    }
  };

  return (
    <AffiliateSalesPartnerPopupView
      title={t("tour.1.banner.title")}
      redirectText={t("affiliateSalesPartner.popup.redirect")}
      okLabel={t("generic.ok")}
      partnerName={partner?.name}
      partnerLogo={partner?.logo}
      fullWidthButton={!isMinDesktop}
      onClose={onClose}
    />
  );
};

export function createAffiliateSalesPartnerPopup() {
  if (!displayAffiliatePartnerPopupBanner()) {
    return;
  }
  if (
    userOnRegistrationConfirmationEmailPage() ||
    userOnRegistrationConfirmatioAccountPage() ||
    userOnMyAccountPage()
  ) {
    return null;
  }
  return <AffiliateSalesPartnerPopup />;
}
